import { Box } from 'components/boxs'
import { Icon } from 'components/icon'
import { TypeFeed } from 'components/typeFeed'
import { Typography } from 'components/typography'
import { VisibilitySettingsBox } from 'components/visibilitySettingsBox'
import { useToast } from 'hooks'
import {
	DropdownFullItemProps,
	Icons,
	OrganizationInformationForm,
	OrganizationInformationFormEditSettings,
	TypographyTypes,
	ViewOrganization
} from 'interfaces'
import { allSettingsTimeTypesData } from 'lib'
import React, { useEffect, useMemo, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import {
	optionMyNetwork,
	optionOTPHasRequired,
	optionsCalendarView,
	optionSeletCSV,
	optionSeletVisibility,
	organizationResource
} from './optionSelect'

import { SettingsSSO } from './settingSSO'

const DescriptionEnabled = (): JSX.Element => (
	<ul className="list-disc pl-5">
		<li>
			Tabs will now become <strong>Cards</strong>, with the possibility to add{' '}
			<strong>menus, submenus, components or redirections</strong>.
		</li>
		<li>
			<strong>Images</strong> can be added next to each card&apos;s name.
		</li>
		<li>
			<strong>Unlimited</strong> cards and menus can be created.
		</li>
		<li>
			<strong>Card images wil be added automatically</strong>, must be updated
			to reflect the card&apos;s content.
		</li>
	</ul>
)

const DescriptionDisabled = (): JSX.Element => (
	<ul className="list-disc pl-5">
		<li>
			The Resources section of the user app will return to it&apos;s{' '}
			<strong>previous interface</strong>.
		</li>
		<li>
			First <strong>4 tabs</strong> will remain if they where created with
			components. Any more tabs created beyond that or without the components
			feature, <strong>will be lost</strong>.
		</li>
		<li>
			<strong>Submenus</strong> and <strong>images</strong> will also{' '}
			<strong>be lost</strong>.
		</li>
	</ul>
)

export const Settings: React.FC<OrganizationInformationFormEditSettings> = ({
	settings,
	editing,
	views,
	settingsOrg
}) => {
	const { setValue, control, watch } =
		useFormContext<OrganizationInformationForm>()
	const data = watch('views')
	const activeResource = watch('settings.active_resource')
	const { showToast } = useToast()
	const [time, setTime] = useState<DropdownFullItemProps>()
	const [dataView, setDataView] = useState<ViewOrganization[] | null>(null)

	useEffect(() => {
		if (views) setDataView(views)
	}, [views])

	useEffect(() => {
		if (editing) {
			setValue('sso', settings)
			setValue('views', views)
			setValue('settings', settingsOrg)
			const response = allSettingsTimeTypesData.find(
				e => e.value === settings?.data_refresh_frecuency_sso?.toString()
			)
			if (response) setTime(response)
		}
	}, [editing, settings])

	useEffect(() => {
		if (time !== undefined) {
			setValue('sso.data_refresh_frecuency_sso', Number(time?.value), {
				shouldValidate: true
			})
		}
	}, [time])

	const validateActiveItems = (element: ViewOrganization[]): boolean => {
		if (!element || element.length === 0) {
			showToast(
				'No views available',
				'Please wait for views to load before continuing',
				'error'
			)
			return false
		}

		const activeItems = element.filter(item => item?.enabled ?? true)

		if (activeItems.length === 0) {
			showToast(
				'All views disabled',
				`You need to enable at least one, please set the most valuable view to your customers`,
				'error'
			)
			return false
		}

		const orders = new Set()
		const hasDuplicates = activeItems.some(item => {
			if (!item || item.order === undefined) {
				return false
			}
			if (orders.has(item.order)) {
				showToast(
					'Repeated Order',
					`Set the correct order for the views that are enabled, can't repeat numbers!.`,
					'error'
				)
				return true
			}
			orders.add(item.order)

			return false
		})

		return !hasDuplicates
	}

	const handleViewChange = (
		index: number,
		updatedView: ViewOrganization
	): void => {
		setDataView(prevViews => {
			const newViews = prevViews ? [...prevViews] : []
			newViews[index] = updatedView
			setValue('views', newViews)
			return newViews
		})
	}

	const messageInformationResource = (): {
		icon: string
		title: string
		labelButtonConfirm: string
		DescriptionComponent: () => JSX.Element
	} => {
		const isActive = String(activeResource) === 'true'
		if (!isActive) {
			return {
				icon: Icons.checkActive,
				title: 'New functionalities will be added',
				labelButtonConfirm: 'Yes, enable!',
				DescriptionComponent: DescriptionEnabled
			}
		}
		return {
			icon: Icons.warningActive,
			title: 'Some information might be lost',
			labelButtonConfirm: 'Yes, disable.',
			DescriptionComponent: DescriptionDisabled
		}
	}

	const messageInformation = useMemo(
		() => messageInformationResource(),
		[activeResource]
	)

	return (
		<>
			<Box className="border-none min-h-[530px] mb-6">
				<>
					<div className="flex flex-row items-center pb-4 gap-2">
						<Icon src={Icons.config} className="w-5 h-5 text-blue-primary" />
						<Typography
							title="Manage App Views:"
							fontLeading="15/18"
							type={TypographyTypes.h3}
							className="text-black-1"
						/>
					</div>

					{dataView && dataView.length > 0 && (
						<div className="grid grid-cols-1 1lg:grid-cols-4 gap-4 sm:grid-cols-2">
							{dataView.map((element, index) => (
								<Controller
									control={control}
									name={`views.${index as number}`}
									rules={{
										validate: () => {
											return validateActiveItems(data)
										}
									}}
									render={({ field: { onChange } }) => (
										<div className="flex flex-col min-w-[32%]">
											<TypeFeed
												element={element}
												key={`${element.name}_${element.id}`}
												handleChange={value => {
													onChange(value)
													handleViewChange(index, value)
												}}
											/>
										</div>
									)}
								/>
							))}
						</div>
					)}
				</>
			</Box>
			<SettingsSSO time={time as DropdownFullItemProps} setTime={setTime} />
			<VisibilitySettingsBox
				option={optionOTPHasRequired}
				control={control}
				name="settings.require_otp"
				titleBox="Enable OTP Verification"
				titleDropdown="Require administrators to enter a four-digit OTP code when logging in"
				requiredConfirmation
				titleModalConfirmation="OTP Verification"
				descriptionModalConfirmation="Only administrators with a valid and active email can log in. A four-digit code will be required. Are you sure you want to continue?"
				iconModalConfirmation={Icons.lock}
			/>
			<VisibilitySettingsBox
				option={optionSeletVisibility}
				control={control}
				name="settings.is_visible"
				titleBox="Hide organization:"
				titleDropdown="Select whether this organization appears in search or not:"
			/>
			{editing && (
				<VisibilitySettingsBox
					option={organizationResource}
					control={control}
					name="settings.active_resource"
					titleBox="Advance Organization Resources:"
					titleDropdown="Select whether this organization have advance resources available for their users"
					iconBox={Icons.dashboardCustomize}
					requiredConfirmation
					whenIsRequiredConfirmation={
						String(activeResource) === 'true' ? 'false' : 'true'
					}
					titleModalConfirmation={messageInformation.title}
					childrenModalConfirmation={messageInformation.DescriptionComponent()}
					iconModalConfirmation={messageInformation.icon}
					labelButtonConfirm={messageInformation.labelButtonConfirm}
					widhtModal="w-[40rem]"
					reverseButton={String(activeResource) === 'true'}
					showCloseButton={false}
					valueType="boolean"
				/>
			)}
			<VisibilitySettingsBox
				option={optionsCalendarView}
				control={control}
				name="settings.active_calendar_view"
				titleBox="Calendar View in the Content Hub"
				titleDropdown="Is the Calendar View in the Content Hub enabled for this Organization?"
				iconBox={Icons.calendarMonth}
				valueType="boolean"
			/>
			<VisibilitySettingsBox
				option={optionSeletCSV}
				control={control}
				name="settings.active_import_csv"
				titleBox="Enable CSV Import or Update Users"
				titleDropdown="Administrators of this organization will be able to update or import users via CSV"
				iconBox={Icons.breakingNews}
			/>
			<VisibilitySettingsBox
				option={optionMyNetwork}
				control={control}
				name="settings.active_my_network"
				titleBox="Enable My Network"
				titleDropdown="Enable or disable the My Network feature for this organization"
				iconBox={Icons.myNetwork}
				valueType="boolean"
			/>
		</>
	)
}
